/**
 * ConnectFlow — Cloud API.
 *
 * Oferece dois caminhos no INTRO:
 *   - Embedded Signup (1-clique) via Tech Provider Configuration ID
 *   - Conexão manual (WABA ID + Phone Number ID + System User Token)
 */

import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import {
  IconKey,
  IconShieldCheck,
  IconTemplate,
  IconLayoutList,
  IconCheck,
  IconAlertTriangle,
  IconExternalLink,
  IconEye,
  IconEyeOff,
  IconBrandMeta,
} from '@tabler/icons-react';
import useCloudApi from '../../../../../WhatsAppCloudApi/hooks/useCloudApi';
import useFacebookSDK from '../../../../../WhatsAppCloudApi/hooks/useFacebookSDK';
import './connectFlow.css';

const STEPS = {
  INTRO: 'intro',
  GUIDE: 'guide',
  FORM: 'form',
  REVIEW: 'review',
  SUBMITTING: 'submitting',
  SUCCESS: 'success',
  ERROR: 'error',
};

const BENEFITS = [
  { icon: IconShieldCheck, text: 'Conexão oficial Meta — sem QR Code, sem risco de ban' },
  { icon: IconTemplate, text: 'Templates de mensagem aprovados pela Meta' },
  { icon: IconLayoutList, text: 'Mensagens interativas com botões e listas' },
];

const GUIDE_STEPS = [
  {
    title: '1. WABA ID (Identificação da conta WhatsApp Business)',
    text: 'No Meta Business Manager, acesse "WhatsApp → Configuração da API". Copie o "Identificação da conta do WhatsApp Business".',
    link: 'https://business.facebook.com/wa/manage/phone-numbers/',
  },
  {
    title: '2. Identificação do número de telefone',
    text: 'Na mesma tela, copie a "Identificação do número de telefone" (geralmente logo abaixo do WABA ID). É numérico.',
    link: 'https://business.facebook.com/wa/manage/phone-numbers/',
  },
  {
    title: '3. Token de acesso (permanente)',
    text: 'Em "Configurações do Negócio → Usuários do Sistema", crie um System User ADMIN. Gere um token com as permissões "whatsapp_business_messaging" e "whatsapp_business_management" e marque expiração "Nunca".',
    link: 'https://business.facebook.com/settings/system-users',
  },
];

const ConnectFlow = ({ agentId, onConnected, onCancel }) => {
  const [step, setStep] = useState(STEPS.INTRO);
  const [form, setForm] = useState({ waba_id: '', phone_number_id: '', access_token: '' });
  const [showToken, setShowToken] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [result, setResult] = useState(null);
  const { connect, connectManual } = useCloudApi(agentId);
  const { startEmbeddedSignup, loading: sdkLoading } = useFacebookSDK();

  const handleEmbedded = async () => {
    setErrorMsg('');
    setStep(STEPS.SUBMITTING);
    try {
      const signupData = await startEmbeddedSignup();
      if (!signupData.code) {
        throw new Error('Código de autorização não recebido da Meta.');
      }
      const resp = await connect({
        auth_code: signupData.code,
        phone_number_id: signupData.phone_number_id,
        waba_id: signupData.waba_id,
      });
      setResult(resp);
      setStep(STEPS.SUCCESS);
    } catch (err) {
      setErrorMsg(err.message || 'Erro ao conectar via Meta.');
      setStep(STEPS.ERROR);
    }
  };

  // Validação client-side conservadora: WABA/Phone IDs são numéricos;
  // token tem comprimento mínimo razoável. NÃO validamos prefixo "EAA*"
  // porque Token de acessos têm formatos variados — backend valida
  // de fato via /debug_token.
  const isFormValid =
    form.waba_id.trim().length > 0 &&
    /^\d+$/.test(form.waba_id.trim()) &&
    form.phone_number_id.trim().length > 0 &&
    /^\d+$/.test(form.phone_number_id.trim()) &&
    form.access_token.trim().length >= 40;

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    setStep(STEPS.SUBMITTING);
    try {
      const resp = await connectManual({
        waba_id: form.waba_id.trim(),
        phone_number_id: form.phone_number_id.trim(),
        access_token: form.access_token.trim(),
      });
      setResult(resp);
      setStep(STEPS.SUCCESS);
    } catch (err) {
      setErrorMsg(err.message || 'Erro ao conectar.');
      setStep(STEPS.ERROR);
    }
  };

  const handleFinish = () => {
    if (onConnected) onConnected(result);
  };

  return (
    <div className="cloudapi-connect">
      {step === STEPS.INTRO && (
        <div className="cloudapi-connect__intro">
          <div className="cloudapi-connect__intro-icon">
            <IconBrandMeta size={32} />
          </div>
          <h3>Conectar WhatsApp Oficial</h3>
          <p style={{ color: '#6b7280', marginBottom: 16 }}>
            Conexão oficial via Meta Business Platform.
          </p>

          <div className="cloudapi-connect__benefits">
            {BENEFITS.map((b, i) => (
              <div key={i} className="cloudapi-connect__benefit">
                <b.icon size={16} style={{ color: '#1877F2' }} />
                <span>{b.text}</span>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 24 }}>
            <button
              type="button"
              onClick={handleEmbedded}
              disabled={sdkLoading}
              style={{
                padding: '12px 20px',
                background: '#1877F2',
                border: 'none',
                borderRadius: 8,
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: sdkLoading ? 'not-allowed' : 'pointer',
                opacity: sdkLoading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <IconBrandMeta size={18} />
              Conectar com Meta (1 clique)
            </button>
           
            <button
              type="button"
              onClick={onCancel}
              className="cloudapi-hero__action-btn"
              style={{ alignSelf: 'flex-end', marginTop: 4 }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {step === STEPS.GUIDE && (
        <div className="cloudapi-connect__guide">
          <h4 style={{ marginBottom: 8 }}>Você precisa de 3 credenciais</h4>
          <p style={{ color: '#6b7280', marginBottom: 16, fontSize: 13 }}>
            Siga os passos abaixo para obtê-las no Meta Business Manager.
          </p>

          {GUIDE_STEPS.map((s) => (
            <div key={s.title} className="cloudapi-connect__guide-step">
              <h5>{s.title}</h5>
              <p>{s.text}</p>
              <a href={s.link} target="_blank" rel="noopener noreferrer">
                <IconExternalLink size={12} /> Abrir Meta Business
              </a>
            </div>
          ))}

          <div
            style={{
              padding: 12,
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 8,
              fontSize: 12,
              color: '#92400e',
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              marginTop: 12,
            }}
          >
            <IconAlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              O token DEVE ser configurado com expiração <strong>"Nunca"</strong>. Tokens
              temporários (60 dias) param de funcionar e o agente fica offline.
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16 }}>
            <button
              type="button"
              onClick={() => setStep(STEPS.INTRO)}
              className="cloudapi-hero__action-btn"
            >
              Voltar
            </button>
            <button
              type="button"
              onClick={() => setStep(STEPS.FORM)}
              style={{
                padding: '10px 20px',
                background: '#1877F2',
                border: 'none',
                borderRadius: 8,
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <IconKey size={14} style={{ marginRight: 6 }} />
              Tenho as credenciais
            </button>
          </div>
        </div>
      )}

      {step === STEPS.FORM && (
        <div className="cloudapi-connect__form">
          <div className="cloudapi-field">
            <label className="cloudapi-field__label">Identificação da conta do WhatsApp Business *</label>
            <input
              type="text"
              className="cloudapi-field__input"
              placeholder="Ex: 123456789012345"
              value={form.waba_id}
              onChange={(e) => update('waba_id', e.target.value)}
              autoFocus
            />
            <p className="cloudapi-field__hint">Apenas dígitos.</p>
          </div>

          <div className="cloudapi-field">
            <label className="cloudapi-field__label">Identificação do número de telefone *</label>
            <input
              type="text"
              className="cloudapi-field__input"
              placeholder="Ex: 987654321098765"
              value={form.phone_number_id}
              onChange={(e) => update('phone_number_id', e.target.value)}
            />
            <p className="cloudapi-field__hint">
              Apenas dígitos. Não é o número de telefone — é o ID do número.
            </p>
          </div>

          <div className="cloudapi-field">
            <label
              className="cloudapi-field__label"
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <span>Token de acesso *</span>
              <button
                type="button"
                onClick={() => setShowToken((v) => !v)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#1877F2',
                  fontSize: 11,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                {showToken ? <IconEyeOff size={12} /> : <IconEye size={12} />}
                {showToken ? ' Ocultar' : ' Mostrar'}
              </button>
            </label>
            <textarea
              className="cloudapi-field__input"
              rows={4}
              placeholder="EAAG... (cole o token completo)"
              value={form.access_token}
              onChange={(e) => update('access_token', e.target.value)}
              style={
                showToken
                  ? { fontFamily: 'monospace', fontSize: 11 }
                  : {
                      WebkitTextSecurity: 'disc',
                      textSecurity: 'disc',
                      fontFamily: 'monospace',
                      fontSize: 11,
                    }
              }
            />
            <p className="cloudapi-field__hint">
              {form.access_token.length} caracteres. Token de acesso permanente — mínimo 40 caracteres.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16 }}>
            <button
              type="button"
              onClick={() => setStep(STEPS.GUIDE)}
              className="cloudapi-hero__action-btn"
            >
              Voltar ao guia
            </button>
            <button
              type="button"
              onClick={() => setStep(STEPS.REVIEW)}
              disabled={!isFormValid}
              style={{
                padding: '10px 20px',
                background: isFormValid ? '#1877F2' : '#9ca3af',
                border: 'none',
                borderRadius: 8,
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: isFormValid ? 'pointer' : 'not-allowed',
              }}
            >
              Revisar
            </button>
          </div>
        </div>
      )}

      {step === STEPS.REVIEW && (
        <div className="cloudapi-connect__review">
          <h4>Revise antes de conectar</h4>
          <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 16 }}>
            Confirme se os dados abaixo correspondem ao número que você quer
            conectar a este agente.
          </p>

          <div className="cloudapi-section">
            <div className="cloudapi-section__row">
              <span className="cloudapi-section__row-label">WABA ID</span>
              <span className="cloudapi-section__row-value">{form.waba_id}</span>
            </div>
            <div className="cloudapi-section__row">
              <span className="cloudapi-section__row-label">Identificação do número de telefone</span>
              <span className="cloudapi-section__row-value">{form.phone_number_id}</span>
            </div>
            <div className="cloudapi-section__row">
              <span className="cloudapi-section__row-label">Token</span>
              <span className="cloudapi-section__row-value">
                {form.access_token.slice(0, 8)}...{form.access_token.slice(-4)} (
                {form.access_token.length} chars)
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16 }}>
            <button
              type="button"
              onClick={() => setStep(STEPS.FORM)}
              className="cloudapi-hero__action-btn"
            >
              Editar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              style={{
                padding: '10px 20px',
                background: '#1877F2',
                border: 'none',
                borderRadius: 8,
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <IconKey size={14} style={{ marginRight: 6 }} />
              Validar e conectar
            </button>
          </div>
        </div>
      )}

      {step === STEPS.SUBMITTING && (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spinner animation="border" variant="primary" />
          <p style={{ marginTop: 16, fontSize: 14, color: '#374151' }}>
            Validando credenciais...
          </p>
          <p style={{ fontSize: 12, color: '#9ca3af' }}>
            Verificando token, registrando número e configurando webhook.
          </p>
        </div>
      )}

      {step === STEPS.SUCCESS && (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: '#dcfce7',
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#16a34a',
            }}
          >
            <IconCheck size={32} />
          </div>
          <h4 style={{ marginBottom: 8 }}>Conectado com sucesso!</h4>
          {result?.phone_number && (
            <p style={{ fontSize: 14, color: '#374151', marginBottom: 4 }}>
              {result.phone_number}
            </p>
          )}
          {result?.business_name && (
            <p style={{ fontSize: 13, color: '#6b7280' }}>{result.business_name}</p>
          )}
          <button
            type="button"
            onClick={handleFinish}
            style={{
              marginTop: 16,
              padding: '10px 20px',
              background: '#1877F2',
              border: 'none',
              borderRadius: 8,
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Continuar
          </button>
        </div>
      )}

      {step === STEPS.ERROR && (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: '#fef2f2',
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#dc2626',
            }}
          >
            <IconAlertTriangle size={32} />
          </div>
          <h4 style={{ marginBottom: 8 }}>Erro ao conectar</h4>
          <p style={{ color: '#6b7280', marginBottom: 16, fontSize: 13 }}>{errorMsg}</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button
              type="button"
              onClick={onCancel}
              className="cloudapi-hero__action-btn"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => setStep(STEPS.FORM)}
              style={{
                padding: '10px 20px',
                background: '#1877F2',
                border: 'none',
                borderRadius: 8,
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Corrigir e tentar novamente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectFlow;
